import React, { useState } from 'react';
import { Share2, Copy, MessageCircle, Facebook, Twitter, Send } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { useLang } from '../contexts/LanguageContext';
import { toast } from 'sonner';

export default function ShareButton({ recipe, lang }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);

  const title = lang === 'ar' ? recipe.title_ar : recipe.title_en;
  const url = typeof window !== 'undefined' ? window.location.href : '';
  const text = `${title} — ${t('appName')}`;

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
      } catch {
        setOpen(true);
      }
    } else {
      setOpen(true);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t('linkCopied'));
      setOpen(false);
    } catch {
      toast.error('Error');
    }
  };

  const enc = encodeURIComponent;
  const channels = [
    { id: 'whatsapp', label: 'WhatsApp', Icon: MessageCircle, href: `https://wa.me/?text=${enc(text + ' ' + url)}`, color: 'bg-[#25D366]' },
    { id: 'twitter', label: 'X (Twitter)', Icon: Twitter, href: `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(url)}`, color: 'bg-black' },
    { id: 'facebook', label: 'Facebook', Icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`, color: 'bg-[#1877F2]' },
    { id: 'telegram', label: 'Telegram', Icon: Send, href: `https://t.me/share/url?url=${enc(url)}&text=${enc(text)}`, color: 'bg-[#229ED9]' },
  ];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          onClick={handleNativeShare}
          data-testid="share-recipe-btn"
          className="inline-flex items-center gap-1 rounded-full bg-white border border-black/5 hover:bg-[#EFEBE1] dark:bg-neutral-800 dark:border-white/10 dark:hover:bg-neutral-700 text-[#2C2420] dark:text-neutral-100 px-4 py-2 text-sm font-medium transition"
          aria-label={t('share')}
        >
          <Share2 className="h-4 w-4" /> {t('share')}
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('shareRecipe')}</DialogTitle>
          <DialogDescription className="sr-only">{t('shareRecipe')}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2 mt-2">
          {channels.map(({ id, label, Icon, href, color }) => (
            <a
              key={id}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`share-${id}`}
              className={`inline-flex items-center gap-2 rounded-2xl px-4 py-3 text-sm font-medium text-white ${color} hover:opacity-90 transition`}
            >
              <Icon className="h-4 w-4" /> {label}
            </a>
          ))}
        </div>
        <Button
          onClick={copyLink}
          data-testid="share-copy-link"
          variant="outline"
          className="w-full mt-2 rounded-full"
        >
          <Copy className="h-4 w-4 me-2" /> {t('copyLink')}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
